import React, { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowBack } from "@material-ui/icons";
import HeaderDescription from "../../../components/commons/HeaderDescription";
import RateAnalysisForm from "./RateAnalysisForm";
import RateAnalysisResults from "./RateAnalysisResults";
import { searchRateAnalysis } from "./rateAnalysisApi";
import {
  transformRateAnalysisResponse,
  validateForm,
  TRIP_TYPES,
  TRIP_TYPE_LABELS,
} from "./utils";
import { toastMessage } from "../../../helpers/toastMessage";
import { useDispatch } from "react-redux";
import ThunksRateModule from "../../../redux/actions/thunks/rateModule";
import {
  buildTransferRatesBody,
  getPlaceLabel,
  validateTransferRatesBody,
} from "./transferRatesBody";

const INITIAL_FORM_VALUES = {
  tripType: TRIP_TYPES.AIRPORT_PICKUP,
  pickup: { name: "" },
  dropoff: { name: "" },
  currency: "EUR",
  flightNumber: "",
  date: "",
  dateTime: "",
  passengerCount: 1,
};

const formatDisplayDate = (date) => {
  if (!date) {
    return "";
  }
  try {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(date);
  }
};

const formatDisplayDateTime = (dateTime) => {
  if (!dateTime) {
    return "";
  }
  try {
    return new Date(dateTime).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateTime);
  }
};

const TripRateAnalysis = ({ vendor }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchSummary, setSearchSummary] = useState(null);
  const [airportDetails, setAirportDetails] = useState([]);
  const [loadingAirportDetails, setLoadingAirportDetails] = useState(false);

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const body = buildTransferRatesBody(formValues);
    const errors = {
      ...validateForm(formValues),
      ...validateTransferRatesBody(body, formValues.tripType),
    };
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    setLoadingAirportDetails(true);
    setResults(null);

    try {
      let response = null;
      if (vendor.slug === "trip.com") {
        const [transferResponse, airportResponse] = await Promise.all([
          dispatch(ThunksRateModule.transferRatesThunk(body)),
          dispatch(
            ThunksRateModule.airportDetailsThunk({
              integrationType: "trip.com",
            })
          ),
        ]);

        if (airportResponse?.data) {
          setAirportDetails(airportResponse.data);
        }

        response = transferResponse;

        if (!response?.data) {
          const message =
            response?.response?.data?.msg ||
            response?.message ||
            "Failed to fetch rate analysis.";
          toastMessage("error", message);
          setResults(null);
          return;
        }
      } else {
        toastMessage("error", "Unsupported platform.");
        return;
      }

      const transformed = transformRateAnalysisResponse(response);
      setResults(transformed);
      setSearchSummary({
        tripType: TRIP_TYPE_LABELS[formValues.tripType],
        pickup: getPlaceLabel(formValues.pickup),
        dropoff: getPlaceLabel(formValues.dropoff),
        airportCode: body.airportCode,
        flightNumber: formValues.flightNumber?.trim().toUpperCase() || "",
        date:
          formValues.tripType === TRIP_TYPES.AIRPORT_PICKUP
            ? formatDisplayDate(formValues.date)
            : formatDisplayDateTime(formValues.dateTime),
        passengerCount: parseInt(formValues.passengerCount, 10),
        vendor: vendor.value,
      });
    } catch (error) {
      toastMessage("error", error.message || "Failed to fetch rate analysis.");
      setResults(null);
    } finally {
      setLoading(false);
      setLoadingAirportDetails(false);
    }
  };

  return (
    <div className="rate-analysis-page">
      <button
        type="button"
        className="rate-analysis-back-btn"
        onClick={() => navigate("/dashboard/rate-analysis")}
      >
        <ArrowBack className="rate-analysis-back-icon" />
        Back to platform selection
      </button>

      <HeaderDescription
        heading={`Rate Analysis — ${vendor.value}`}
        description={`Compare service levels and vendor pricing for airport transfers on ${vendor.value}.`}
      />

      <div className="rate-analysis-vendor-badge">
        <img src={vendor.circleIcon} alt={vendor.value} />
        <img
          src={vendor.mainImage}
          alt={vendor.value}
          className="rate-analysis-vendor-badge-logo"
        />
      </div>

      <div className="bg-white main-section-spacing border-radius-8 rate-analysis-form-section">
        <h2 className="font-16 font-600 mb-20 m-0">Search Parameters</h2>
        <RateAnalysisForm
          values={formValues}
          errors={formErrors}
          loading={loading}
          onChange={handleFieldChange}
          onSubmit={handleSubmit}
        />
      </div>

      {loading && (
        <div className="rate-analysis-loading bg-white main-section-spacing border-radius-8">
          <div className="rate-analysis-loading-spinner" />
          <p className="font-14 font-600 m-0">Searching available rates...</p>
          <p className="font-12 text-light-grey m-0">
            Comparing vendors and service levels on {vendor.value}
          </p>
        </div>
      )}

      {!loading && results && (
        <div className="">
          <RateAnalysisResults
            data={results}
            currency={formValues.currency}
            searchSummary={searchSummary}
            airportDetails={airportDetails}
            loadingAirportDetails={loadingAirportDetails}
            tripType={formValues.tripType}
          />
        </div>
      )}
    </div>
  );
};

export default TripRateAnalysis;
